import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Share2, Facebook, Twitter, Linkedin, MessageSquare, Eye, Copy } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface SocialPreview {
  platform: string;
  title: string;
  description: string;
  image: string;
  url: string;
  author?: string;
  dimensions: string;
}

const SocialSharePreviewTool: React.FC = () => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [previews, setPreviews] = useState<SocialPreview[]>([]);

  useSEO({
    title: 'Social Share Preview Tool - Preview Social Media Posts',
    description: 'Preview how your content will appear when shared on social media platforms. Test Facebook, Twitter, LinkedIn previews before sharing.',
    keywords: 'social share preview, facebook preview, twitter preview, linkedin preview, social media preview tool',
    canonical: 'https://snap-tools.online/social/social-share-preview'
  });

  const generatePreviews = () => {
    if (!url.trim() || !title.trim()) {
      toast.error('Please enter at least a URL and title');
      return;
    }

    const defaultImage = imageUrl || 'https://picsum.photos/1200/630?random=4';
    
    const socialPreviews: SocialPreview[] = [
      {
        platform: 'Facebook',
        title: title.substring(0, 100),
        description: description.substring(0, 300) || 'Check out this amazing content!',
        image: defaultImage,
        url: url,
        dimensions: '1200 × 630 pixels'
      },
      {
        platform: 'Twitter',
        title: title.substring(0, 70),
        description: description.substring(0, 200) || 'Discover something new!',
        image: defaultImage,
        url: url,
        dimensions: '1200 × 675 pixels'
      },
      {
        platform: 'LinkedIn',
        title: title.substring(0, 150),
        description: description.substring(0, 300) || 'Professional content worth sharing.',
        image: defaultImage,
        url: url,
        dimensions: '1200 × 627 pixels'
      },
      {
        platform: 'WhatsApp',
        title: title.substring(0, 65),
        description: description.substring(0, 160) || 'Interesting content to check out!',
        image: defaultImage,
        url: url,
        dimensions: '300 × 300 pixels'
      }
    ];

    setPreviews(socialPreviews);
    toast.success('Social previews generated!');
  };

  const copyUrl = (platform: string) => {
    navigator.clipboard.writeText(url);
    toast.success(`${platform} share URL copied!`);
  };

  const loadExample = () => {
    setUrl('https://snap-tools.online');
    setTitle('Amazing Social Media Tools - Snap Tools');
    setDescription('Discover powerful online tools for social media, image editing, PDF conversion, and more. Free, fast, and easy to use.');
    setImageUrl('https://picsum.photos/1200/630?random=5');
    toast.success('Example content loaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <Share2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Social Share Preview Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Preview how your content will appear when shared on different social media platforms. Optimize your posts before sharing.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL *
                  </label>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Your amazing title"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your content..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={generatePreviews}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Generate Previews
              </button>
              <button
                onClick={loadExample}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Load Example
              </button>
            </div>
          </div>

          {/* Previews */}
          {previews.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="grid md:grid-cols-2 gap-6 mb-8"
            >
              {previews.map((preview, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      {preview.platform === 'Facebook' && <Facebook className="w-5 h-5 text-blue-600" />}
                      {preview.platform === 'Twitter' && <Twitter className="w-5 h-5 text-blue-400" />}
                      {preview.platform === 'LinkedIn' && <Linkedin className="w-5 h-5 text-blue-700" />}
                      {preview.platform === 'WhatsApp' && <MessageSquare className="w-5 h-5 text-green-600" />}
                      {preview.platform}
                    </h3>
                    <button
                      onClick={() => copyUrl(preview.platform)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <img
                      src={preview.image}
                      alt="Preview"
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-4 bg-gray-50">
                      <div className="text-xs text-gray-500 mb-1">{new URL(preview.url).hostname}</div>
                      <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{preview.title}</h4>
                      <p className="text-sm text-gray-600 line-clamp-3">{preview.description}</p>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-500">
                    Recommended image size: {preview.dimensions}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Optimization Tips</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Use high-quality, relevant images</li>
                <li>• Keep titles concise and compelling</li>
                <li>• Write descriptions that encourage clicks</li>
                <li>• Test different variations</li>
                <li>• Ensure images load quickly</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Image Specifications</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Facebook:</strong> 1200 × 630 pixels</li>
                <li>• <strong>Twitter:</strong> 1200 × 675 pixels</li>
                <li>• <strong>LinkedIn:</strong> 1200 × 627 pixels</li>
                <li>• <strong>WhatsApp:</strong> 300 × 300 pixels</li>
                <li>• Use JPG or PNG format</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SocialSharePreviewTool;
